import { useEffect } from 'react';
import type { ReactElement } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition, SlotGroupResult } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
  usePropertyAddressUrl,
  usePropertyField,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  LinkedInIcon,
  TikTokIcon,
} from '@vibesites/assets';
import type { IconProps } from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy alongside it for use on
// light primary backgrounds. Source SVG mirrors `repli_white.svg` with the
// wordmark's `fill:#fff` swapped to `#1a1a1a` (matches the dark color used
// by the tokens generator's `getContrastTextColor`).
import repliBlackLogoUrl from '../assets/repli_black.svg';
// Aion Management wordmarks used as the footer "by" logo
// fallback when a property hasn't uploaded company mono / color logos yet.
// White for dark primary backgrounds; gray for light ones (same contrast
// split as the Repli / Multihub marks).
import aionWhiteLogoUrl from '../assets/aion_white_logo.png';
import aionGrayLogoUrl from '../assets/aion_gray_logo.png';

// ============================================================================
// Contrast text color
// ----------------------------------------------------------------------------
// Mirrors `getContrastTextColor` from `@vibesites/tokens` (defined in
// `vendor/vibesites/tokens/generator.ts`, currently module-private and not
// re-exported, so we replicate the exact logic here verbatim — including the
// `0.55` luminance threshold and the `'#ffffff'` / `'#1a1a1a'` return values).
// Using the same function keeps logo color choice (white vs black) in lockstep
// with the auto-computed `--vibe-color-text-on-primary` CSS variable, so the
// footer's text and logos always pick the same contrast side.
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
const TEXT_ON_PRIMARY_DARK = '#1a1a1a';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns the contrasting text color (`'#ffffff'` or `'#1a1a1a'`) for a given
 * background hex. Identical to `getContrastTextColor` in `@vibesites/tokens`.
 * Falls back to the white-on-dark side when the input can't be parsed, which
 * matches how the tokens generator treats unknown / missing primary colors.
 */
function getContrastTextColor(bgHex: string | null | undefined): string {
  if (!bgHex) return TEXT_ON_PRIMARY_LIGHT;
  const luminance = getRelativeLuminance(bgHex);
  // Favor white text on medium tones (gold, tan, dusty blue) where dark text is
  // technically higher-contrast but reads as heavy/muddy. Flip to dark only on
  // genuinely light backgrounds (pastels, cream, near-white).
  return luminance > 0.55 ? TEXT_ON_PRIMARY_DARK : TEXT_ON_PRIMARY_LIGHT;
}

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/**
 * NJ 2-1-1 emergency resources only apply to New Jersey properties. Accepts
 * common state values ("NJ", "N.J.", "New Jersey") and returns false when the
 * state is missing (company-theme / no-property preview) — so the block stays
 * hidden by default.
 */
function isNewJerseyState(state: string | null | undefined): boolean {
  if (!state) return false;
  const normalized = state.trim().toLowerCase().replace(/\./g, '');
  return normalized === 'nj' || normalized === 'new jersey';
}

/** Builds a tel: href from a phone string; returns null when no digits remain. */
function toTelUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : null;
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// Footer address sits in a narrow centered column (`max-w-[220px]`). Long
// "Street, City, ST ZIP" strings wrap mid-zip without a deliberate break —
// split before the locality so city / state / zip land on a second line when
// the full string won't fit on one. Same pattern as NeighborhoodMap popups.
function stripCountry(address: string): string {
  return address.replace(/,?\s*USA\.?\s*$/i, '').trim();
}

function splitAddressLines(address: string): [string, string] | [string] {
  const clean = stripCountry(address);
  if (!clean) return [''];
  // Street, City, ST ZIP
  const withZip = clean.match(/^(.+),\s*([^,]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)$/i);
  if (withZip) return [withZip[1].trim(), withZip[2].trim()];
  // Street, City, ST, ZIP
  const zipComma = clean.match(/^(.+),\s*([^,]+,\s*[A-Z]{2}),\s*(\d{5}(?:-\d{4})?)$/i);
  if (zipComma) return [zipComma[1].trim(), `${zipComma[2].trim()} ${zipComma[3]}`];
  return [clean];
}

function formatFooterAddress(raw: string): ReactElement {
  const lines = splitAddressLines(raw);
  if (lines.length === 2) {
    return (
      <>
        <span className="block">{lines[0]}</span>
        <span className="block">{lines[1]}</span>
      </>
    );
  }
  return <>{lines[0]}</>;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided entries by canonical day key so we can walk the full
  // week below and fill in any missing day as "Closed".
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!byDay.has(key)) byDay.set(key, entry);
  }

  // Render every day of the week in canonical order (Mon–Sun), plus any
  // non-standard day labels supplied by the data after them. A day absent
  // from the collection is treated as closed rather than dropped.
  const extraKeys = [...byDay.keys()].filter((k) => !DAY_ORDER.includes(k));
  const allKeys = [...DAY_ORDER, ...extraKeys];

  const groups: GroupedHours[] = [];

  for (const key of allKeys) {
    const entry = byDay.get(key);
    const dayLabel = DAY_LABELS[key] || entry?.day || key;
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry?.day || key).slice(0, 3);
    const isClosed = !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the responsive renderer can swap them at the tablet
      // breakpoint without re-deriving the grouping.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// ---- Social links ----
// One slot group per platform. Each is a plain link group (url + hidden
// _target), defaulting to the matching property_*_url field. Icons only
// render when a URL is present, so communities that haven't set a given
// network simply show nothing under "Follow Us".
const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const twitterLinkDef: SlotGroupDefinition = {
  label: 'Twitter / X',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_twitter_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const linkedinLinkDef: SlotGroupDefinition = {
  label: 'LinkedIn',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_linkedin_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_tiktok_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Aion Management' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Dual leasing-office addresses (Fox Ridge + Lakeview Park). Matches the
// live reference site at liveatfoxridge.com, which lists both communities
// under the footer contact column.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Fox Ridge Address',
  fields: {
    name: { label: 'Community Name', type: 'text', defaultValue: 'Fox Ridge Apartments' },
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '817 Walden Ct, Limerick, PA 19468',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place/business/address
    // fields). An explicit override from the editor still takes precedence.
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const address2LinkDef: SlotGroupDefinition = {
  label: 'Lakeview Park Address',
  fields: {
    name: { label: 'Community Name', type: 'text', defaultValue: 'Lakeview Park Apartments' },
    text: {
      label: 'Address Text',
      type: 'text',
      defaultValue: '601 Lakeview Dr, Royersford, PA 19468',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://www.google.com/maps/search/?api=1&query=601+Lakeview+Dr,+Royersford,+PA+19468',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ---- NJ emergency contact (shown only when property_address_state is NJ) ----
const serviceManagerPhoneDef: SlotGroupDefinition = {
  label: 'Service Manager Phone',
  description:
    'Emergency service manager phone displayed inside the emergency contact block. Falls back to the property call-tracking phone if left blank.',
  fields: {
    text: { label: 'Phone', type: 'phone', defaultField: 'property_call_tracking_phone' },
  },
};

const needHelpLinkDef: SlotGroupDefinition = {
  label: 'Need Help Link',
  description:
    '"Need Help? Start Here!" call-to-action for the emergency contact block. Links to NJ 2-1-1.',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Need Help? Start Here!' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://nj211.org/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const emergencyBadgeDef: SlotGroupDefinition = {
  label: 'Emergency Badge Image',
  description:
    'Optional emergency-services badge image (e.g. NJ 2-1-1) shown at the bottom of the emergency contact block.',
  fields: {
    image: {
      label: 'Badge Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/7b1da63d/dms3rep/multi/opt/NJ211-Logo-web-352px-79w.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'New Jersey 2-1-1 — Help Starts Here' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://nj211.org/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};


// ============================================================================
// Social link
// ----------------------------------------------------------------------------
// Renders a single social icon link, or nothing when no URL is set. Kept as a
// small helper so the six platform blocks in the render stay declarative — the
// `<EditableSlotGroup>` wrappers around each still carry literal groupPaths for
// static schema extraction.
// ============================================================================

function SocialLink({
  url,
  target,
  Icon,
  label,
}: {
  url: string | null | undefined;
  target: string | null | undefined;
  Icon: (props: IconProps) => ReactElement;
  label: string;
}) {
  if (!hasValue(url)) return null;
  return (
    <a
      href={url}
      target={target || '_blank'}
      rel="noopener noreferrer"
      aria-label={label}
      className="text-[var(--vibe-color-text-on-primary)]/70 hover:text-primary-foreground transition-colors"
    >
      <Icon size={20} color="currentColor" aria-label={label} />
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');
  const copyrightName = useContentToken('footer.copyright_name');
  const emergencyPropertyName = useContentToken('footer.emergency_property_name');
  const propertyAddressState = usePropertyField('property_address_state');
  // NJ-only emergency contact block (2-1-1). Hidden when state is missing
  // (company-theme preview) or anything other than New Jersey.
  const showEmergencyContact = isNewJerseyState(propertyAddressState);

  // ---- Contrast-driven logo variant selection ----
  // Decide whether text-on-primary should be white or dark using the same
  // `getContrastTextColor` logic the tokens generator uses to compute
  // `--vibe-color-text-on-primary`. We then pick mono_white / mono_black logo
  // defaults (and the management-company + Repli marks) to match that
  // decision, so the footer logos always sit on the correct contrast side
  // regardless of how dark/light the property's primary brand color is. The
  // runtime-adjusted slot definitions are passed to both `useSlotGroup` and
  // `EditableSlotGroup` so the editor surfaces the same default the renderer
  // is using.
  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const runtimeLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: {
            ...logoSlotDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  const runtimeManagedByLogoDef: SlotGroupDefinition = useWhiteOnPrimary
    ? managedByLogoDef
    : {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: {
            ...managedByLogoDef.fields.image,
            defaultField: 'property_company_mono_black_logo',
          },
        },
      };

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const managedByLogoRaw = useSlotGroup('footer.managed_by_logo', runtimeManagedByLogoDef);
  // Cascade through company brand assets when the contrast-matched mono
  // defaultField is unset on the property — same spirit as Header's logo
  // fallback. Last resort is the bundled Aion wordmark so this company
  // theme always shows a management logo next to the label (matching how
  // Multihub / Repli always rendered in the credit row).
  const companyMonoWhite = usePropertyField('property_company_mono_white_logo');
  const companyMonoBlack = usePropertyField('property_company_mono_black_logo');
  const companyLogo = usePropertyField('property_company_logo');
  const companyIcon = usePropertyField('property_company_icon');
  const aionFallbackLogo = useWhiteOnPrimary ? aionWhiteLogoUrl : aionGrayLogoUrl;
  const managedByImage =
    managedByLogoRaw.image ||
    (useWhiteOnPrimary
      ? companyMonoWhite || companyLogo || companyMonoBlack || companyIcon
      : companyMonoBlack || companyLogo || companyMonoWhite || companyIcon) ||
    aionFallbackLogo;
  const managedByLogo: SlotGroupResult = { ...managedByLogoRaw, image: managedByImage };
  const facebook = useSlotGroup('footer.facebook', facebookLinkDef);
  const instagram = useSlotGroup('footer.instagram', instagramLinkDef);
  const twitter = useSlotGroup('footer.twitter', twitterLinkDef);
  const linkedin = useSlotGroup('footer.linkedin', linkedinLinkDef);
  const tiktok = useSlotGroup('footer.tiktok', tiktokLinkDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const address2Link = useSlotGroup('footer.address_2', address2LinkDef);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const serviceManagerPhone = useSlotGroup('footer.service_manager_phone', serviceManagerPhoneDef);
  const needHelpLink = useSlotGroup('footer.need_help_link', needHelpLinkDef);
  const emergencyBadge = useSlotGroup('footer.emergency_badge', emergencyBadgeDef);

  const serviceManagerHref = toTelUrl(serviceManagerPhone.text) || toTelUrl(phone);
  const serviceManagerDisplay =
    formatPhone(serviceManagerPhone.text) || formatPhone(phone) || '(555) 555-5555';

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe IQ widget is injected at runtime by an external script.
  // It (a) ships under several DOM signatures across versions and (b) sets its
  // own inline `style="z-index: …"` on its outer container after mount, which
  // beats anything a stylesheet declares. The CSS rule further down in the
  // render covers the common selectors as a cheap first pass; this effect is
  // the authoritative fix.
  //
  // Approach: watch document.body for any added/changed node. For each element
  // whose id/class/src/title/name matches a RentCafe-IQ-ish pattern, find the
  // outermost `position: fixed` ancestor (the actual floating container, which
  // is what controls stacking against the rest of the page) and pin its
  // z-index to the max signed 32-bit int with `!important`. `setProperty` with
  // the `important` priority is the only way to override an inline style set
  // by the widget's own JS.
  //
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point without having to
  // introduce a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Patterns covering the DOM signatures Yardi ships the RentCafe IQ /
    // "RentCafe Connect" webchat widget under. The current production build
    // uses the `rcc-app-*` class prefix and the `conversations-webchat-toggle`
    // / `webchat-toggle-button` launcher; older / alternate builds have
    // surfaced under rentcafe / rciq / CRM IQ / Yardi / Yvenue branding, so
    // we keep those entries too as a defense against future rebrands.
    const PATTERNS = [
      /\brcc-app/i,
      /\bwebchat-toggle/i,
      /conversations-webchat/i,
      /rentcafe/i,
      /rciq/i,
      /\bcrm[-_ ]?iq\b/i,
      /\biq[-_ ]?(chat|bot)/i,
      /yardi/i,
      /yvenue/i,
    ];

    const Z = '2147483647';

    function attrHaystack(el: Element): string {
      const id = el.id || '';
      const cls = typeof el.className === 'string'
        ? el.className
        : ((el.className as unknown as { baseVal?: string })?.baseVal || '');
      const src = el.getAttribute('src') || '';
      const title = el.getAttribute('title') || '';
      const name = el.getAttribute('name') || '';
      // Include data-* attributes — Yardi widgets sometimes only brand via those.
      let data = '';
      for (const a of Array.from(el.attributes)) {
        if (a.name.startsWith('data-')) data += ` ${a.name} ${a.value}`;
      }
      return `${id} ${cls} ${src} ${title} ${name}${data}`;
    }

    function looksLikeRciq(el: Element): boolean {
      const hay = attrHaystack(el);
      return PATTERNS.some(p => p.test(hay));
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!looksLikeRciq(el)) return;
      // Pin the matched element itself (in case it's already the fixed container)
      // and also the outermost fixed ancestor (covers wrappers / portals).
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (fixedAncestor && fixedAncestor !== el) pin(fixedAncestor);
    }

    function scanSubtree(root: ParentNode) {
      // Scope the query so we don't enumerate the whole document on every tick.
      const candidates = root.querySelectorAll('iframe, div, aside, section, button');
      for (const c of Array.from(candidates)) processCandidate(c);
      // The root node itself can also be a match (querySelectorAll skips it).
      if (root instanceof Element) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only react to attribute changes that could affect identification or
      // stacking — anything else (e.g. inline transforms during animation) is
      // noise we want to ignore.
      attributeFilter: ['id', 'class', 'src', 'title', 'name', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      className="text-primary-foreground"
      style={{ backgroundColor: 'var(--vibe-color-primary)' }}
    >

      {/* ---- Main Footer Content ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-center">

          {/* Column 1: Follow Us */}
          {/* Social icons render only when the property has that network set —
              communities without socials simply show the heading alone. Each
              platform keeps a literal-path <EditableSlotGroup> so the editor can
              still surface it once a URL exists. */}
          <div className="flex flex-col items-center gap-5">
            <RichTextContent
              path="footer.follow_us_label"
              defaultValue="<p>Follow Us</p>"
              as="p"
              className="font-body text-[13px] uppercase tracking-[0.2em] text-primary-foreground"
            />
            <div className="flex items-center justify-center gap-4">
              {hasValue(facebook.url) && (
                <EditableSlotGroup groupPath="footer.facebook" definition={facebookLinkDef}>
                  <SocialLink url={facebook.url} target={facebook._target} Icon={FacebookIcon} label="Facebook" />
                </EditableSlotGroup>
              )}
              {hasValue(instagram.url) && (
                <EditableSlotGroup groupPath="footer.instagram" definition={instagramLinkDef}>
                  <SocialLink url={instagram.url} target={instagram._target} Icon={InstagramIcon} label="Instagram" />
                </EditableSlotGroup>
              )}
              {hasValue(twitter.url) && (
                <EditableSlotGroup groupPath="footer.twitter" definition={twitterLinkDef}>
                  <SocialLink url={twitter.url} target={twitter._target} Icon={TwitterIcon} label="Twitter" />
                </EditableSlotGroup>
              )}
              {hasValue(linkedin.url) && (
                <EditableSlotGroup groupPath="footer.linkedin" definition={linkedinLinkDef}>
                  <SocialLink url={linkedin.url} target={linkedin._target} Icon={LinkedInIcon} label="LinkedIn" />
                </EditableSlotGroup>
              )}
              {hasValue(tiktok.url) && (
                <EditableSlotGroup groupPath="footer.tiktok" definition={tiktokLinkDef}>
                  <SocialLink url={tiktok.url} target={tiktok._target} Icon={TikTokIcon} label="TikTok" />
                </EditableSlotGroup>
              )}
            </div>
          </div>

          {/* Column 2: Logo, Contact, Copyright */}
          <div className="flex flex-col items-center text-center gap-4">
            <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-20 w-auto max-w-[220px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-primary-foreground">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {/* Phone */}
            <a
              href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
              className="dniphone dniphonehref font-body text-[15px] text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors"
            >
              <EditableToken
                path="footer.phone"
                defaultField="property_call_tracking_phone"
                defaultValue="(555) 555-5555"
              >
                {formatPhone(phone)}
              </EditableToken>
            </a>

            {/* Addresses — matches liveatfoxridge.com / reference footer:
                "Leasing Office Located At:" + two labeled community links. */}
            <div className="flex flex-col items-center gap-3">
              <RichTextContent
                path="footer.leasing_office_label"
                defaultValue="<p>Leasing Office Located At:</p>"
                as="p"
                className="font-body text-[13px] uppercase tracking-[0.2em] text-primary-foreground"
              />

              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <div className="flex flex-col items-center gap-0.5 max-w-[240px]">
                  {hasValue(addressLink.name) && (
                    <span className="font-body text-[13px] font-semibold uppercase tracking-[0.08em] text-primary-foreground">
                      {addressLink.name}
                    </span>
                  )}
                  <a
                    href={addressLink.url || propertyAddressUrl || '#'}
                    target={addressLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="font-body text-[15px] leading-snug text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors block"
                  >
                    {formatFooterAddress(addressLink.text || '817 Walden Ct, Limerick, PA 19468')}
                  </a>
                </div>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="footer.address_2" definition={address2LinkDef}>
                <div className="flex flex-col items-center gap-0.5 max-w-[240px]">
                  {hasValue(address2Link.name) && (
                    <span className="font-body text-[13px] font-semibold uppercase tracking-[0.08em] text-primary-foreground">
                      {address2Link.name}
                    </span>
                  )}
                  <a
                    href={
                      address2Link.url ||
                      'https://www.google.com/maps/search/?api=1&query=601+Lakeview+Dr,+Royersford,+PA+19468'
                    }
                    target={address2Link._target || '_blank'}
                    rel="noopener noreferrer"
                    className="font-body text-[15px] leading-snug text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors block"
                  >
                    {formatFooterAddress(
                      address2Link.text || '601 Lakeview Dr, Royersford, PA 19468',
                    )}
                  </a>
                </div>
              </EditableSlotGroup>
            </div>

            {/* Copyright — allow wrap on narrow viewports; nowrap overflow-clipped
                long dual-community names (e.g. Fox Ridge and Lakeview Park). */}
            <p className="font-body text-[13px] text-[var(--vibe-color-text-on-primary)]/60 mt-1 max-w-full text-balance">
              ©{' '}
              <EditableToken
                path="footer.copyright_name"
                defaultField="property_name"
                defaultValue="Our Community"
              >
                {copyrightName || 'Our Community'}
              </EditableToken>
              . All rights reserved.
            </p>
          </div>

          {/* Column 3: Office Hours */}
          <div className="flex flex-col items-center gap-3">
            <RichTextContent
              path="footer.office_hours_label"
              defaultValue="<p>Office Hours</p>"
              as="p"
              className="font-body text-[13px] uppercase tracking-[0.2em] text-primary-foreground"
            />
            {groupedHours.length > 0 ? (
              <dl className="space-y-2 w-full max-w-[280px]">
                {groupedHours.map((group, index) => (
                  <div key={index} className="flex justify-between gap-6 font-body text-sm text-[var(--vibe-color-text-on-primary)]/80">
                    <dt>
                      {/* Full names on mobile and desktop; abbreviated on tablet
                          (md → lg) where the 3-column grid leaves the hours
                          column too narrow for "Monday - Friday". */}
                      <span className="md:hidden lg:inline">{group.label}</span>
                      <span className="hidden md:inline lg:hidden">{group.shortLabel}</span>
                    </dt>
                    <dd className="text-right">{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-body text-sm text-[var(--vibe-color-text-on-primary)]/40">Office hours will be displayed here.</p>
            )}
          </div>

        </div>

        {/* ============================================================ */}
        {/* Emergency contact information block (NJ properties only)     */}
        {/* Hidden by default; rendered when property_address_state is NJ */}
        {/* ============================================================ */}
        {showEmergencyContact ? (
          <div className="mt-12 md:mt-16 flex flex-col items-center text-center md:items-start md:text-left gap-1.5">
            <RichTextContent
              path="footer.emergency_heading"
              defaultValue="<p>Emergency Contact Information | Información de Contacto en caso de Emergencia</p>"
              as="p"
              className="font-body text-[15px] leading-snug text-primary-foreground"
            />
            <p className="font-body text-[15px] leading-snug text-[var(--vibe-color-text-on-primary)]/80">
              <EditableToken path="footer.emergency_property_name">
                {emergencyPropertyName || 'Our Community'}
              </EditableToken>
              {' Service Manager: '}
              <EditableSlotGroup groupPath="footer.service_manager_phone" definition={serviceManagerPhoneDef}>
                <a
                  href={serviceManagerHref || '#'}
                  className="dniphone dniphonehref text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors"
                >
                  {serviceManagerDisplay}
                </a>
              </EditableSlotGroup>
            </p>

            <EditableSlotGroup groupPath="footer.need_help_link" definition={needHelpLinkDef}>
              <a
                href={needHelpLink.url || 'https://nj211.org/'}
                target={needHelpLink._target || '_blank'}
                rel="noopener noreferrer"
                className="font-body text-[15px] font-bold text-primary-foreground hover:opacity-80 transition-opacity mt-1"
              >
                {needHelpLink.text || 'Need Help? Start Here!'}
              </a>
            </EditableSlotGroup>

            <EditableSlotGroup groupPath="footer.emergency_badge" definition={emergencyBadgeDef} className="mt-3 inline-block">
              {(() => {
                const badgeSrc =
                  emergencyBadge.image ||
                  'https://irp.cdn-website.com/7b1da63d/dms3rep/multi/opt/NJ211-Logo-web-352px-79w.png';
                const badgeAlt = emergencyBadge.alt || 'New Jersey 2-1-1 — Help Starts Here';
                const badgeImg = (
                  <VibesitesImage
                    url={badgeSrc}
                    alt={badgeAlt}
                    sizes="100px"
                    className="h-14 w-auto max-w-[100px] object-contain"
                  />
                );
                return emergencyBadge.url ? (
                  <a
                    href={emergencyBadge.url}
                    target={emergencyBadge._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={badgeAlt}
                    className="inline-block opacity-90 hover:opacity-100 transition-opacity"
                  >
                    {badgeImg}
                  </a>
                ) : (
                  badgeImg
                );
              })()}
            </EditableSlotGroup>
          </div>
        ) : null}
      </div>

      {/* ---- Bottom Bar: Credits + Compliance + Legal ---- */}
      {/* Same primary background as the main footer (no darkened strip).
          Management company logo on the left, compliance icons + legal links
          centered, Repli on the right. On mobile the three grid cells stack
          and center. */}
      <div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col items-center gap-5">

            {/* Credits (logos only) + compliance + legal.
                On md+, center column is `auto` so legal links get their natural
                width (equal 1/3 cols were wrapping "Privacy Policy" on tablet). */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-6 w-full">

            {/* Managed by — property management company logo (left) */}
            <EditableSlotGroup
              groupPath="footer.managed_by_logo"
              definition={runtimeManagedByLogoDef}
              className="flex justify-center md:justify-start"
            >
              <a
                href={managedByLogo.url || '#'}
                target={managedByLogo._target || '_blank'}
                rel="noopener noreferrer"
                aria-label={managedByLogo.alt || 'Aion Management'}
                className="inline-flex items-center opacity-90 hover:opacity-100 transition-opacity"
              >
                <VibesitesImage
                  url={managedByLogo.image || aionFallbackLogo}
                  alt={managedByLogo.alt || 'Aion Management'}
                  height={36}
                  className="h-9 w-auto max-w-[180px] object-contain"
                  sizes="180px"
                />
              </a>
            </EditableSlotGroup>

            {/* Compliance icons + legal nav (center) */}
            <div className="flex flex-col items-center gap-3 md:px-2">
              <div className="flex items-center justify-center gap-5 text-[var(--vibe-color-text-on-primary)]/70">
                <FairHousingIcon size={26} color="currentColor" />
                <PetsAllowedIcon size={26} color="currentColor" />
                <WheelchairIcon size={26} color="currentColor" />
              </div>
              <nav className="flex flex-nowrap items-center justify-center gap-x-2 font-body text-[13px] text-primary-foreground whitespace-nowrap" aria-label="Legal links">
                <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                  <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                    {accessibilityLink.text || 'Accessibility'}
                  </a>
                </EditableSlotGroup>
                <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">·</span>
                <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                  <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                    {privacyPolicyLink.text || 'Privacy Policy'}
                  </a>
                </EditableSlotGroup>
                <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">·</span>
                <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                  <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                    {sitemapLink.text || 'Sitemap'}
                  </a>
                </EditableSlotGroup>
              </nav>
            </div>

            {/* Website Design by — Repli (right) */}
            <EditableSlotGroup
              groupPath="footer.design_by_logo"
              definition={designByLogoDef}
              className="flex justify-center md:justify-end"
            >
              <a
                href={designByLogo.url || 'https://www.repli360.com/'}
                target={designByLogo._target || '_blank'}
                rel="noopener noreferrer"
                aria-label={designByLogo.alt || 'Repli'}
                className="inline-flex items-center opacity-90 hover:opacity-100 transition-opacity"
              >
                <VibesitesImage
                  url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                  alt={designByLogo.alt || 'Repli Logo'}
                  height={36}
                  className="h-9 w-auto"
                  sizes="160px"
                  priority
                />
              </a>
            </EditableSlotGroup>

            </div>
          </div>
        </div>
      </div>

      {/* ---- Global z-index fallback for the RentCafe IQ chatbot ----
          This is a cheap first pass to cover the common DOM signatures Yardi
          has shipped (iframe, container div, launcher button). The
          authoritative fix lives in the `useEffect` above this `return` — it
          uses a MutationObserver and inline `!important` to handle widget
          variants whose selectors we don't know and to beat the inline
          z-index the widget sets via its own JS after mount. */}
      <style>{`
        /* Production RentCafe Connect / IQ build (confirmed against live DOM):
           the floating wrap is .rcc-app-cta-wrap, the launcher is
           #conversations-webchat-toggle.webchat-toggle-button. We target the
           wrap (and the inner controls layer) so the entire stacking context
           gets lifted, not just the bubble. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        [class*="rcc-app-"],
        [id*="conversations-webchat"],
        [class*="webchat-toggle"],
        /* Legacy / alternate Yardi signatures, kept as a hedge against
           rebrands and per-property build variants. */
        iframe[src*="rentcafeiq"],
        iframe[src*="rentcafe-iq"],
        iframe[src*="rentcafe.com/iq"],
        iframe[src*="yardi"],
        iframe[src*="yvenue"],
        iframe[title*="RentCafe IQ" i],
        iframe[title*="Rent Cafe IQ" i],
        iframe[title*="CRM IQ" i],
        iframe[title*="webchat" i],
        [id^="rciq"],
        [id*="rentcafeiq"],
        [id*="rentcafe-iq"],
        [id*="rentcafe_iq"],
        [id*="iq-chat"],
        [id*="iqchat"],
        [id*="iq-bot"],
        [id*="iqbot"],
        [class^="rciq-"],
        [class*=" rciq-"],
        [class*="rentcafeiq"],
        [class*="rentcafe-iq"],
        [class*="rentcafe_iq"],
        [class*="iq-chat"],
        [class*="iqchat"],
        [class*="yardi-chat"],
        [class*="yvenue"],
        [data-rciq],
        [data-rentcafeiq] {
          z-index: 2147483647 !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.follow_us_label': {
      label: 'Follow Us Label',
      type: 'richtext',
      defaultValue: '<p>Follow Us</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.leasing_office_label': {
      label: 'Leasing Office Label',
      type: 'richtext',
      defaultValue: '<p>Leasing Office Located At:</p>',
    },
    'footer.copyright_name': {
      label: 'Copyright Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Our Community',
    },
    'footer.emergency_heading': {
      label: 'Emergency Contact Heading',
      type: 'richtext',
      defaultValue:
        '<p>Emergency Contact Information | Información de Contacto en caso de Emergencia</p>',
    },
    'footer.emergency_property_name': {
      label: 'Emergency Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Our Community',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.facebook': facebookLinkDef,
    'footer.instagram': instagramLinkDef,
    'footer.twitter': twitterLinkDef,
    'footer.linkedin': linkedinLinkDef,
    'footer.tiktok': tiktokLinkDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.address_2': address2LinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.service_manager_phone': serviceManagerPhoneDef,
    'footer.need_help_link': needHelpLinkDef,
    'footer.emergency_badge': emergencyBadgeDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
